import { Minus, Plus } from '@atom-learning/icons'
import * as React from 'react'

import { styled } from '~/styled'
import { cn } from '~/styled'
import { getFieldIconSize } from '~/utilities/style/get-icon-size'

import { Input } from '../input/Input'
import { NumberInputStepper } from './NumberInputStepper'

export interface NumberInputProps {
  name: string
  min?: number
  max?: number
  step?: number
  value?: number
  defaultValue?: number
  disabled?: boolean
  readonly?: boolean
  size?: 'sm' | 'md' | 'lg'
  appearance?: 'standard' | 'modern'
  theme?: 'white' | 'grey'
  emphasis?: 'bold'
  onValueChange?: (value: number) => void
  stepperButtonLabels?: { increment?: string; decrement?: string }
  disabledTooltipContent?: { increment?: string; decrement?: string }
  className?: string
}

const NumberInputContainer = styled('div', {
  base: ['flex'],
  variants: {
    appearance: {
      standard: [],
      modern: ['gap-px']
    },
    emphasis: {
      bold: ['gap-0']
    }
  }
})

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      defaultValue = 0,
      onValueChange,
      min = 0,
      max = Number.MAX_SAFE_INTEGER,
      step = 1,
      disabled: isDisabled = false,
      readonly: isReadOnly = false,
      size = 'md',
      stepperButtonLabels: stepperButtonLabelsProp,
      disabledTooltipContent: disabledTooltipContentProp,
      className,
      appearance = 'standard',
      theme,
      emphasis,
      ...rest
    },
    ref
  ): React.JSX.Element => {
    const [internalValue, setInternalValue] = React.useState<number>(
      value || defaultValue
    )
    React.useEffect(() => {
      // Update the internal value to match what is passed in.
      if (typeof value !== 'undefined') setInternalValue(value)
    }, [value])

    const inputRef = React.useRef<HTMLInputElement | null>(null)

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    const iconSize = React.useMemo(() => getFieldIconSize(size), [size])

    const stepperButtonLabels = {
      increment: 'increment',
      decrement: 'decrement',
      ...stepperButtonLabelsProp
    }

    const disabledTooltipContent = {
      decrement: `Cannot enter values below ${min}`,
      increment: `Cannot enter values above ${max}`,
      ...disabledTooltipContentProp
    }

    const isAtMax = internalValue >= max
    const isAtMin = internalValue <= min

    const clamp = React.useCallback(
      (internalValue: number) => Math.min(Math.max(internalValue, min), max),
      [max, min]
    )

    const updateValue = React.useCallback(
      (newValue: number) => {
        onValueChange?.(newValue)
        setInternalValue(newValue)
      },
      [onValueChange]
    )

    const onInputChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const parsedValue = Number(event.target.value.replace(/\D/g, ''))
        updateValue(parsedValue)
      },
      [updateValue]
    )

    const increment = React.useCallback(() => {
      if (isAtMax || isReadOnly) return
      inputRef?.current?.focus()
      const newValue = Number(internalValue) + step
      updateValue(clamp(newValue))
    }, [clamp, isAtMax, isReadOnly, step, updateValue, internalValue])

    const decrement = React.useCallback(() => {
      if (isAtMin || isReadOnly) return
      inputRef?.current?.focus()
      const newValue = Number(internalValue) - step
      updateValue(clamp(newValue))
    }, [clamp, isAtMin, isReadOnly, min, step, updateValue, internalValue])

    const onKeyDown = React.useCallback(
      (event: React.KeyboardEvent) => {
        if (event.nativeEvent.isComposing) return

        /**
         * Keyboard Accessibility
         *
         * We want to increase or decrease the input's value
         * based on if the user the arrow keys.
         *
         * @see https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-17
         */
        const eventKey = event.key

        const keyMap: Record<string, React.KeyboardEventHandler> = {
          ArrowUp: increment,
          ArrowRight: increment,
          ArrowDown: decrement,
          ArrowLeft: decrement,
          Home: () => updateValue(min),
          End: () => updateValue(max)
        }

        const action = keyMap[eventKey]

        if (action) {
          event.preventDefault()
          action(event)
        }
      },
      [increment, decrement, updateValue, min, max]
    )

    const hasError = internalValue < min || internalValue > max
    const {
      state: externalState,
      'aria-invalid': externalAriaInvalid,
      ...restProps
    } = rest as { state?: 'error'; 'aria-invalid'?: boolean } & typeof rest

    const inputProps: React.ComponentProps<typeof Input> = {
      type: 'number',
      value: internalValue,
      ...restProps,
      onChange: onInputChange,
      onKeyDown,
      size,
      appearance,
      theme,
      state: externalState || (hasError ? 'error' : undefined),
      'aria-invalid': externalAriaInvalid || hasError || undefined,
      className: cn(
        'rounded-none',
        'w-16',
        '[&_>_input]:text-center',
        'disabled:opacity-30',
        'disabled:pointer-events-none',
        emphasis === 'bold' && [
          'rounded-1',
          '[&_>_input[aria-invalid=true]]:text-danger',
          'focus-within:[&_>_input]:text-grey-1000'
        ],
        emphasis === 'bold' &&
          appearance === 'standard' && [
            'border-none',
            '[&:has([aria-invalid=true])]:border-2',
            '[&:has([aria-invalid=true])]:border-danger',
            'focus-within:border-2',
            'focus-within:border-blue-800'
          ]
      ),
      ref: inputRef,
      readOnly: isReadOnly,
      disabled: isDisabled,
      'aria-valuemin': min,
      'aria-valuemax': max,
      'aria-valuenow': internalValue,
      role: 'spinbutton'
    }

    return (
      <NumberInputContainer
        appearance={appearance}
        emphasis={emphasis}
        className={className}
      >
        <NumberInputStepper
          onClick={decrement}
          icon={Minus}
          className={cn(
            emphasis !== 'bold' && ['border-r-none', 'rounded-r-none']
          )}
          size={iconSize}
          fieldAppearance={appearance}
          fieldTheme={theme}
          emphasis={emphasis}
          disabled={isAtMin || isDisabled}
          showTooltip={isAtMin && !isDisabled}
          disabledTooltipContent={disabledTooltipContent.decrement}
          label={stepperButtonLabels.decrement}
        />
        <Input {...inputProps} />
        <NumberInputStepper
          onClick={increment}
          icon={Plus}
          className={cn(
            emphasis !== 'bold' && ['border-l-none', 'rounded-l-none']
          )}
          size={iconSize}
          fieldAppearance={appearance}
          fieldTheme={theme}
          emphasis={emphasis}
          disabled={isAtMax || isDisabled}
          showTooltip={isAtMax && !isDisabled}
          disabledTooltipContent={disabledTooltipContent.increment}
          label={stepperButtonLabels.increment}
        />
      </NumberInputContainer>
    )
  }
)

NumberInput.displayName = 'NumberInput'
